/**
 * Quests and goals — the longer horizon. A quest holds goals; each goal has a
 * target date and can be dropped onto the Dashplan.
 */
import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { Plus, Target, Trash2, CalendarClock, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { todayKey, minuteOfDay, dateKey } from "@/lib/dates";

export default function QuestsPage() {
  const { toast } = useToast();
  const quests = useQuery(api.quests.list, {});
  const createQuest = useMutation(api.quests.create);
  const removeQuest = useMutation(api.quests.remove);
  const createGoal = useMutation(api.quests.createGoal);
  const updateGoal = useMutation(api.quests.updateGoal);
  const removeGoal = useMutation(api.quests.removeGoal);
  const scheduleGoal = useMutation(api.dashplan.scheduleGoal);

  const [questOpen, setQuestOpen] = useState(false);
  const [title, setTitle] = useState("");

  const schedule = async (goalId: Id<"goals">) => {
    const startMinute = Math.min(1380, Math.ceil(minuteOfDay() / 30) * 30);
    await scheduleGoal({ goalId, date: todayKey(), startMinute });
    toast({ title: "Added to today's Dashplan" });
  };

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="flex items-center gap-2">
        <h1 className="page-title text-2xl font-semibold flex-1">Quests</h1>
        <Dialog open={questOpen} onOpenChange={setQuestOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="w-4 h-4 mr-1.5" />
              New quest
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>New quest</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={async (event) => {
                event.preventDefault();
                if (!title.trim()) return;
                await createQuest({ title: title.trim() });
                setTitle("");
                setQuestOpen(false);
              }}
              className="space-y-4"
            >
              <div className="space-y-1.5">
                <Label htmlFor="quest-title">What are you going after?</Label>
                <Input
                  id="quest-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ship Dashbase v3"
                  autoFocus
                  required
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={!title.trim()}>
                  Create
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {quests === undefined && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}

      {quests?.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            No quests yet. A quest is the bigger thing your dailies add up to.
          </CardContent>
        </Card>
      )}

      {quests?.map((quest) => {
        const pct =
          quest.goalCount === 0
            ? 0
            : Math.round((quest.completedGoalCount / quest.goalCount) * 100);

        return (
          <Card key={quest._id}>
            <CardHeader className="pb-3">
              <div className="flex items-start gap-2">
                <Target className="w-4 h-4 mt-1 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-base">{quest.title}</CardTitle>
                  {quest.description && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {quest.description}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Delete ${quest.title}`}
                  className="shrink-0 text-destructive hover:text-destructive"
                  onClick={() => removeQuest({ id: quest._id })}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {quest.goalCount > 0 && (
                <div className="pt-2 space-y-1">
                  <Progress value={pct} />
                  <p className="text-xs text-muted-foreground tabular-nums">
                    {quest.completedGoalCount} of {quest.goalCount} goals · {pct}%
                  </p>
                </div>
              )}
            </CardHeader>

            <CardContent className="space-y-2">
              {quest.goals.map((goal) => {
                const done = goal.status === "completed";
                return (
                  <div
                    key={goal._id}
                    className="flex items-center gap-2 rounded-lg border border-border p-2.5"
                  >
                    <button
                      type="button"
                      aria-label={
                        done ? `Reopen ${goal.title}` : `Complete ${goal.title}`
                      }
                      aria-pressed={done}
                      onClick={() =>
                        updateGoal({
                          id: goal._id,
                          status: done ? "pending" : "completed",
                        })
                      }
                      className={cn(
                        "w-4 h-4 rounded-full border shrink-0 flex items-center justify-center",
                        done
                          ? "bg-primary border-primary"
                          : "border-muted-foreground/50 hover:border-foreground",
                      )}
                    >
                      {done && (
                        <Check className="w-2.5 h-2.5 text-primary-foreground" />
                      )}
                    </button>

                    <span
                      className={cn(
                        "text-sm flex-1 truncate",
                        done && "line-through text-muted-foreground",
                      )}
                    >
                      {goal.title}
                    </span>

                    <span className="text-xs text-muted-foreground font-mono shrink-0">
                      {dateKey(new Date(goal.targetDate))}
                    </span>

                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Schedule ${goal.title} today`}
                      onClick={() => schedule(goal._id)}
                    >
                      <CalendarClock className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Delete ${goal.title}`}
                      className="text-destructive hover:text-destructive"
                      onClick={() => removeGoal({ id: goal._id })}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}

              <AddGoal
                onAdd={async (goalTitle, targetDate) => {
                  const target = new Date(targetDate);
                  await createGoal({
                    questId: quest._id,
                    title: goalTitle,
                    targetDate: target.getTime(),
                    quarter: Math.floor(target.getMonth() / 3) + 1,
                  });
                }}
              />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function AddGoal({
  onAdd,
}: {
  onAdd: (title: string, targetDate: string) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  // Default the target four weeks out — near enough to matter, far enough to
  // be achievable.
  const [target, setTarget] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 28);
    return dateKey(d);
  });

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        if (!title.trim()) return;
        await onAdd(title.trim(), target);
        setTitle("");
      }}
      className="flex gap-2 pt-1"
    >
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Add a goal…"
        className="h-9"
        aria-label="New goal"
      />
      <Input
        type="date"
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        className="h-9 w-[150px] shrink-0"
        aria-label="Target date"
      />
      <Button type="submit" size="sm" disabled={!title.trim()}>
        <Plus className="w-4 h-4" />
      </Button>
    </form>
  );
}
